'use client';

import React, { type ComponentPropsWithoutRef, memo, useMemo } from 'react';
import { Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  type CardProps,
} from '@/components/ui/card';
import Flex from '@/components/ui/layout/flex';
import Wrapper from '@/components/ui/layout/wrapper';
import { MotionLink } from '@/components/ui/Motion';
import ProjectGallery from '@/components/ui/ProjectGallery';
import {
  TypographyH2,
  TypographyH3,
  TypographySmall,
} from '@/components/ui/typography';
import { URLS } from '@/lib/consts';
import { cls, cn } from '@/lib/utils';
import type { ProjectName } from '../../../types';

export interface GalleryCardProperties extends ComponentPropsWithoutRef<'div'> {
  projectName: ProjectName;
  title?: string;
  description?: string;
  sub?: string;
  url?: string;
  showCTA?: boolean;
  variant?: CardProps['variant'];
}

function GalleryCard({
  projectName,
  title = '',
  description = '',
  sub = '',
  className = '',
  url = URLS.PROJECTS,
  showCTA = true,
  variant = 'default',
}: GalleryCardProperties) {
  const showHeader = useMemo(() => {
    if (title) return true;

    if (description) return true;

    if (sub) return true;

    return false;
  }, [title, description, sub]);

  return (
    <Card className={cn(cls`w-full`, className)} variant={variant}>
      {showHeader && (
        <CardHeader className={cls`p-2 md:p-5 text-center`}>
          {sub && (
            <TypographyH3 className="font-bold text-primary">{sub}</TypographyH3>
          )}

          {title && (
            <TypographyH2 className={cls`border-b-0`}>{title}</TypographyH2>
          )}

          {description && <TypographySmall>{description}</TypographySmall>}
        </CardHeader>
      )}

      <CardContent
        className={cls`
          flex flex-col justify-center items-center
          gap-2 md:gap-5
          p-2 md:p-5
          pt-0
        `}
      >
        <Wrapper padding="p-4 md:p-6" asChild>
          <Flex
            direction="column"
            items="center"
            gap="gap-4"
            className="w-full"
          >
            {/* Gallery */}
            <ProjectGallery projectName={projectName} />

            {/* CTA Button */}
            {showCTA && (
              <>
                <Button asChild className={cls`w-fit`}>
                  <MotionLink href={url}>
                    <Eye className={cls`mr-2`} />
                    View More
                  </MotionLink>
                </Button>
              </>
            )}
          </Flex>
        </Wrapper>
      </CardContent>
    </Card>
  );
}

export default memo(GalleryCard);
